import * as dayjs from 'dayjs';
import { PassSeatSelectionLock } from '../models/serializable/ticketing-state/pass-seat-selection-lock.js';
import { TrueTime } from '../time/true-time.js';
import { ISeatSelectionEntry, PermanentSeatSelectionEntry, ReservedSeatSelectionEntry, TemporarySeatSelectionEntry } from '../models/serializable/ticketing-state/seat-selection-entry.js';

export class AdminPassSeatSelectionLockHelper {
  private readonly entries: ISeatSelectionEntry[];

  constructor(
      lock: PassSeatSelectionLock,
  ) {
    this.entries = lock.entries;
  }

  tryCreateTemporarySeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(TemporarySeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    const entry = TemporarySeatSelectionEntry.create({
      sessionId,
      seats,
      creationDate: now,
      expirationDate: now.add(30, 'minutes'),
    });

    if(entryIndex >= 0) {
      this.entries[entryIndex] = entry;
    }
    else {
      this.entries.push(entry);
    }

    return true;
  }

  tryCreatePermanentSeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(PermanentSeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    if(entryIndex >= 0) {
      lockedSeats.clear();
      addAllSeatsAsLocked(lockedSeats, this.entries[entryIndex].seats);
      addAllSeatsAsLocked(lockedSeats, seats);

      this.entries[entryIndex].seats.length = 0;
      this.entries[entryIndex].seats.push(...lockedSeats);
    }
    else {
      this.entries.push(PermanentSeatSelectionEntry.create({
        sessionId,
        seats,
        creationDate: now,
      }));
    }

    return true;
  }

  tryCreateReservedSeatLock(
      sessionId: string,
      seats: number[],
      reservationId: string,
      now?: dayjs.Dayjs,
      expirationDate?: dayjs.Dayjs,
  ): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(
        ReservedSeatSelectionEntry.assertType(entry) &&
        entry.sessionId == sessionId &&
        entry.reservationId == reservationId
      ) {
        entryIndex = ei;
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    if(entryIndex >= 0) {
      lockedSeats.clear();
      addAllSeatsAsLocked(lockedSeats, this.entries[entryIndex].seats);
      addAllSeatsAsLocked(lockedSeats, seats);

      this.entries[entryIndex].seats.length = 0;
      this.entries[entryIndex].seats.push(...lockedSeats);
    }
    else {
      this.entries.push(ReservedSeatSelectionEntry.create({
        sessionId,
        seats,
        reservationId,
        creationDate: now,
        expirationDate,
      }));
    }

    return true;
  }

  findAllPermanentlyLockedSeats(now?: dayjs.Dayjs): number[] {
    now ??= TrueTime.now();

    const seats: number[] = [];

    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(this.entries[ei].isExpired(now)) {
        continue;
      }

      if(PermanentSeatSelectionEntry.assertType(this.entries[ei]) ||
          ReservedSeatSelectionEntry.assertType(this.entries[ei])
      ) {
        for(let si = this.entries[ei].seats.length - 1; si >= 0; si--) {
          seats.push(this.entries[ei].seats[si]);
        }
      }
    }

    return seats;
  }

  tryRemovePermanentSeatLock(seats: number[], now?: dayjs.Dayjs): void {
    now ??= TrueTime.now();

    this.unlockAllExpiredEntries(now);

    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      const entry = this.entries[ei];

      if(PermanentSeatSelectionEntry.assertType(entry) ||
          ReservedSeatSelectionEntry.assertType(entry)
      ) {
        for(let si = entry.seats.length - 1; si >= 0; si--) {
          for(let k = 0; k < seats.length; k++) {
            if(seats[k] == entry.seats[si]) {
              entry.seats.splice(si, 1);
              break;
            }
          }
        }
      }

      if(entry.seats.length <= 0) {
        this.entries.splice(ei, 1);
      }
    }
  }

  private unlockAllExpiredEntries(now: dayjs.Dayjs): void {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(this.entries[ei].isExpired(now)) {
        this.entries.splice(ei, 1);
      }
    }
  }
}

export class CustomerPassSeatSelectionLockHelper {
  private readonly entries: ISeatSelectionEntry[];

  constructor(
      lock: PassSeatSelectionLock,
  ) {
    this.entries = lock.entries;
  }

  tryCreateTemporarySeatLock(sessionId: string, seats: number[], now?: dayjs.Dayjs): boolean {
    now ??= TrueTime.now();
    this.unlockAllExpiredEntries(now);

    const lockedSeats = new Set<number>();
    let entryIndex: number = -1;

    for(let ei = 0; ei < this.entries.length; ei++) {
      const entry = this.entries[ei];
      if(TemporarySeatSelectionEntry.assertType(entry) && entry.sessionId == sessionId) {
        entryIndex = ei;
        continue;
      }

      addAllSeatsAsLocked(lockedSeats, entry.seats);
    }

    if(isAnySeatAlreadyLocked(lockedSeats, seats)) {
      return false;
    }

    const entry = TemporarySeatSelectionEntry.create({
      sessionId,
      seats,
      creationDate: now,
      expirationDate: now.add(30, 'minutes'),
    });

    if(entryIndex >= 0) {
      this.entries[entryIndex] = entry;
    }
    else {
      this.entries.push(entry);
    }

    return true;
  }

  private unlockAllExpiredEntries(now: dayjs.Dayjs): void {
    for(let ei = this.entries.length - 1; ei >= 0; ei--) {
      if(this.entries[ei].isExpired(now)) {
        this.entries.splice(ei, 1);
      }
    }
  }
}

function addAllSeatsAsLocked(allLockedSeats: Set<number>, seats: number[]): void {
  for(let si = 0; si < seats.length; si++) {
    allLockedSeats.add(seats[si]);
  }
}

function isAnySeatAlreadyLocked(allLockedSeats: Set<number>, seats: number[]): boolean {
  for(let si = 0; si < seats.length; si++) {
    if(allLockedSeats.has(seats[si])) {
      return true;
    }
  }

  return false;
}
